import { IELTDataSettings } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { ReactNode, useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { isExistsChangedSteps } from '../../../../helpers/changedStep/isExistsChangedSteps'
import { getEltCoverageLabel } from '../../../../helpers/getEltCoverageLabel'
import { getFormedEltTranslation } from '../../../../helpers/getFormedEltTranslation'
import { IChangedSteps } from '../../../../models/IChangedSteps'
import { EltValueTypeSwitcher } from '../eltValueTypeButtons/EltValueTypeButtons'
import { FinalizeConfirmDialog } from '../finalizeConfirmDialog/FinalizeConfirmDialog'
import { RefreshFormConfirmDialog } from '../refreshFormConfirmDialog/RefreshFormConfirmDialog'
import { ValueTypeConfirmDialog } from '../valueTypeConfirmDialog/ValueTypeConfirmDialog'

import styles from './ActionButtons.module.scss'

interface IProps {
  children?: ReactNode
  currentStep: number
  changedSteps: IChangedSteps
  onSave: (step?: number) => void
  finalizeVersion: (saveData?: boolean) => void
  formIsFinalized: boolean
  forceUpdateFormToActualData: () => void
  dataSettings: IELTDataSettings
  onNetNdpChange: (netNdpValue: ActualsValueTypes) => void
}

export const ActionButtons = ({
  children,
  onSave,
  currentStep,
  finalizeVersion,
  changedSteps,
  formIsFinalized,
  forceUpdateFormToActualData,
  onNetNdpChange,
  dataSettings,
}: IProps) => {
  /** -------------------- State ----------------------- */

  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState({
    show: false,
    onlyConfirmation: false,
  })
  const [showRefreshConfirmation, setShowRefreshConfirmation] = useState(false)
  const [newValueTypeConfirmation, setNewValueTypeConfirmation] = useState(ActualsValueTypes.None)

  /** -------------------- Hooks ----------------------- */

  const {
    jbpSettings: { fYearELT, fQuarterELT },
  } = useJbpSettings()

  /** -------------------- Variables ----------------------- */

  const isExistsUnsavedData = isExistsChangedSteps(changedSteps)
  const showSaveButton = isExistsUnsavedData
  const refreshPeriod = getEltCoverageLabel(fYearELT, fQuarterELT)

  const isShowValueTypeConfirmDialog = useMemo(() => {
    return newValueTypeConfirmation !== ActualsValueTypes.None
  }, [newValueTypeConfirmation])

  /** -------------------- Methods ----------------------- */

  const onRefreshClick = useCallback(() => {
    setShowRefreshConfirmation(true)
  }, [])

  const closeRefreshConfirmationDialog = useCallback(() => {
    setShowRefreshConfirmation(false)
  }, [])

  const closeValueTypeConfirmationDialog = useCallback(() => {
    setNewValueTypeConfirmation(ActualsValueTypes.None)
  }, [])

  const onNetNdpChangeHandle = useCallback(
    (netNdpValue: ActualsValueTypes) => {
      const isNdp = netNdpValue === ActualsValueTypes.Ndp
      if (isNdp) {
        setNewValueTypeConfirmation(ActualsValueTypes.Ndp)
      } else {
        onNetNdpChange(netNdpValue)
      }
    },
    [onNetNdpChange]
  )

  const onAgreeValueTupe = useCallback(() => {
    onNetNdpChange(newValueTypeConfirmation)
  }, [newValueTypeConfirmation, onNetNdpChange])

  /** -------------------- User actions handlers ----------------------- */

  // This functionality is not needed yet

  // const onFinalizeClick = () => () => {
  //   if (isExistsUnsavedData) {
  //     setShowFinalizeConfirm({ show: true, onlyConfirmation: false })
  //   } else {
  //     setShowFinalizeConfirm({ show: true, onlyConfirmation: true })
  //   }
  // }

  /** -------------------- Render ----------------------- */
  return (
    <div className={styles.actions}>
      <RenderWhen condition={!formIsFinalized}>
        <Button className={styles.btn} color={'white'} onClick={onRefreshClick}>
          <Translation
            text={getFormedEltTranslation('refresh-form')}
            replaceParams={[{ key: '{period}', value: refreshPeriod }]}
          />
        </Button>
      </RenderWhen>

      <EltValueTypeSwitcher
        valueType={dataSettings.NetNdp}
        onChangeValueType={onNetNdpChangeHandle}
        valueTypes={dataSettings.ActualsValueTypes}
      />

      <RenderWhen condition={showSaveButton && !formIsFinalized}>
        <Button color={'white'} className={styles.btn} onClick={() => onSave(currentStep)}>
          <Translation text={getFormedEltTranslation('save-changed-data')} />
        </Button>
      </RenderWhen>

      {children}

      <RefreshFormConfirmDialog
        show={showRefreshConfirmation}
        onAgree={forceUpdateFormToActualData}
        closeDialog={closeRefreshConfirmationDialog}
      />
      <FinalizeConfirmDialog
        finalizeVersion={(saveData) => {
          setShowFinalizeConfirm({ show: false, onlyConfirmation: false })
          finalizeVersion(saveData)
        }}
        onlyConfirmation={showFinalizeConfirm.onlyConfirmation}
        show={showFinalizeConfirm.show}
        closeDialog={() => setShowFinalizeConfirm({ show: false, onlyConfirmation: false })}
      />
      <ValueTypeConfirmDialog
        show={isShowValueTypeConfirmDialog}
        onAgree={onAgreeValueTupe}
        closeDialog={closeValueTypeConfirmationDialog}
      />
    </div>
  )
}
